/**
 * Maps
 *
 * A collection of re-usable functions that's used for Google Maps
 * API work
 *
 * @since 0.5
 * @package \app\abstract
 *
 */

/**
 * intersectionLookup
 *
 * Given formatted_address, loop through address components and format address
 * e.g. formatted_address: "SK-14, Perdue, SK S0K 3C0, Canada" => "Saskatchewan 14, Perdue, Saskatchewan"
 *
 * @since 0.5
 *
 * @param {object} place place object that is returned by the Google Maps API
 * @return {object} the reformatted road object
 *
 */
export function formatAddress(place) {
    // assuming place.formatted_address, place.address_components, and place.geometry exist
    var formatted = place.formatted_address;
    var data = {};
    var location = {};
    var fac = formatAddressComponents(
        place.address_components,
        formatted,
        data
    );
    var loc = formatGeometry(place.geometry, location);

    return {
        address: fac.formatted,
        data: fac.data,
        location: loc.data,
    };
}

/**
 * getPostalCode
 *
 * Fallback function to geocode for an address to lookup postal code
 *
 * @since 0.5
 *
 * @param {object} address_lat_long Address object that comes back from the Google Autocomplete
 * @param {object} componentObj React component to set state
 *
 * @return {n/a}
 *
 */
export function getPostalCode(address_lat_long, componentObj) {
    geoCodePostalCode(address_lat_long, componentObj);

    function geoCodePostalCode(address, component) {
        let geocoder = new google.maps.Geocoder();

        geocoder.geocode(
            { location: address.location },
            function (results, status) {
                let geo_address = false; // Assuming that first one always is the right address

                if (results !== null) {
                    geo_address = results[0]; // Assuming that first one always is the right address
                }

                if (typeof geo_address == 'object') {
                    const addresses = geo_address.address_components;
                    let postal_code = '';

                    // Loop through and find postal_code
                    addresses.forEach((address) => {
                        if (address.types.includes('postal_code')) {
                            postal_code = address.long_name;
                        }
                    });

                    address.data.postal_code = postal_code;

                    component.setState({
                        manualVote: false,
                        roadObject: address,
                        disabled: false,
                    });
                }
            }
        );
    }
}

/**
 * formatAddressComponents
 *
 * Callback function to format the place's address components object.
 * jQuery loop axed.
 *
 * @since 0.5
 *
 * @param {object} address_components the nice name of the place returned by GM API
 * @param {string} formatted the nice name of the place returned by GM API
 * @param {object} data the object that stores the reformatted address components
 *
 * @return {object} place's reformatted address components
 *
 */
export function formatAddressComponents(address_components, formatted, data) {
    address_components.map((ac) => {
        if (ac.types.includes('street_number')) {
            formatted = formatted.replace(ac.long_name, '');
            formatted = formatted.replace(ac.short_name, '');
        } else if (ac.types.includes('route')) {
            // e.g. SK-14 => Saskatchewan 14
            formatted = formatted.replace(ac.short_name, ac.long_name);

            data.route = ac.long_name;
        } else if (ac.types.includes('premise')) {
            /**
             * `CN Tower, 104 Avenue Northwest, Edmonton`
             * will get translated to:
             * `104 Avenue Northwest, Edmonton`
             */
            formatted = formatted.replace(ac.long_name + ',', '');
            formatted = formatted.replace(ac.short_name + ',', '');
        } else if (ac.types.includes('country')) {
            // e.g. Canada =>
            formatted = formatted.replace(', ' + ac.long_name, '');
            formatted = formatted.replace(' ' + ac.long_name, '');
            formatted = formatted.replace(', ' + ac.short_name, '');
            formatted = formatted.replace(' ' + ac.short_name, '');
        } else if (ac.types.includes('postal_code')) {
            formatted = formatted.replace(ac.long_name + ',', '');
            formatted = formatted.replace(ac.short_name + ',', '');
            formatted = formatted.replace(ac.long_name, '');
            formatted = formatted.replace(ac.short_name, '');

            // Postal Code
            data.postalcode = ac.long_name;
        } else if (ac.types.includes('administrative_area_level_1')) {
            formatted = formatted.replace(ac.short_name + ',', ac.long_name);
            formatted = formatted.replace(ac.short_name, ac.long_name);

            // Province
            data.province = ac.long_name;
        } else if (ac.types.includes('locality')) {
            // City | Locality
            data.locality = ac.long_name;
        } else if (ac.types.includes('administrative_area_level_2')) {
            // we will use this OR `aal3` IF `locality` is missing
            data.aal2 = ac.long_name;
        } else if (ac.types.includes('administrative_area_level_3')) {
            // we will use this OR `aal2` IF `locality` is missing
            data.aal3 = ac.long_name;
        }

        formatted = formatted.trim();
    });

    return { formatted: formatted, data: data };
}

/**
 * roadLookup
 *
 * Callback method for the autocomplete API call when the input field value is entered
 *
 * @since 0.5
 *
 * @param {object} input input element object
 * @return {object} roadAutocomplete the road object returned by the Google Maps API
 *
 */
export function roadLookup(input) {
    const roadAutocomplete = new google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: {
            country: 'CA',
        },
    });

    return roadAutocomplete;
}

/**
 * formatGeometry
 *
 * Callback function to extract the place's latitude and longitude from the geometry object.
 *
 * @since 0.5
 *
 * @param {object} geometryObj the place's geometry object returned by GM API
 * @param {object} data the empty object that will store the latitude and longitude of the place
 * @return {object} place's extracted latitude and longitude
 *
 */
export function formatGeometry(geometryObj, data) {
    data.lat = geometryObj.location.lat();
    data.lng = geometryObj.location.lng();

    return { data: data };
}

// Helper function to validate if 2 roads intersect
function validateIntersection(results, status = false) {
    let valid = false;

    // If status is ok and there are results returned
    if (status == 'OK' && results.length == 1) {
        const types = results[0].types;

        types.forEach(function (type) {
            if (type == 'intersection') {
                valid = true;
            }
        });
    }

    return valid;
}

export function getIntersectionCoordinates(
    roadInputObject,
    formattedObject,
    intersectionObject
) {
    const roadObject = roadInputObject.state.roadObject;
    const crossroadObject = formattedObject;
    let intersection = new google.maps.Geocoder();

    intersection.geocode(
        {
            address:
                roadInputObject.state.roadObject.data.route +
                ' and ' +
                crossroadObject.address +
                ', CA',
        },
        function (results, status) {
            // 5. Check if the two roads are a `type` intersection
            // Validating the results if it is a valid intersection or not
            const validIntersection = validateIntersection(results, status);

            // If it is a valid intersection then record the coordinates of the intersection so that it can be passed up to the map
            if (validIntersection) {
                intersectionObject = {
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                };
            } else {
                //If not a valid intersection then leave the coordinates as the initial road.
                intersectionObject = roadObject.location;
            }

            // 6. Pass the lat and long of the intersection to reposition the marker to the parent <StepOne/>
            roadInputObject.props.onCrossroadUpdate(
                formattedObject,
                intersectionObject,
                validIntersection
            );
        }
    );
}
